import { getActionCenterPaths } from "imports/desktopPaths"

import CampaignActionTypes, {
    loadCampaignTypes,
    patchCampaignTypes,
    loadPlaceholdersActionDict,
    validatePlaceholdersActionDict,
    loadGrassrootsRegistrationPageActionDict,
    createGrassrootsRegistrationPageActionDict,
    patchGrassrootsRegistrationPageActionDict,
    patchCampaignInteractionTypesActionDict,
    validateRegulationIdActionDict,
} from "shared/campaign/campaign-action-types"
import { selectTextToValidate } from "shared/campaign/campaign-selectors"
import { sendUserAction } from "shared/user-actions/user-actions-action-creators"

import searchifyActionTypes from "shared/search/searchify/action-types"
import { segue } from "global-action-creators"
import swal from "sweetalert"
import * as Sentry from "@sentry/browser"
import * as outboxActionCreators from "outbox/action-creators"
import * as outboundTextingActionCreators from "app/static/frontend/outbound-texting/action-creators"

// for testing go to /campaigns/new/ to create new campaign
// or to /campaigns/detail/3419/ or ../3442/
import * as djangioActionCreators from "shared/djangio/action-creators"

import swalConfigs from "campaign/swalConfigs"

export const campaignKwargs = {
    creator: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
    limited_to_teams: Userdata.default_limited_to_teams,
    name: "",
    organization: DjangIO.app.userdata.models.Organization.foreignKey(Userdata.organization_id),
    projects: [],
    // set every new campaign as draft
    status: DjangIO.app.grassroots.campaign.types.CampaignStatus.draft.value,
    url: "",
    footer_html: "",
}

export const initializeCampaign = (kwargs = {}) =>
    djangioActionCreators.resourcePostList(
        DjangIO.app.grassroots.campaign.models.Campaign.objects.filter({ full_dehydrate_phone_numbers: true }),
        loadCampaignTypes,
        {
            ...campaignKwargs,
            ...kwargs,
        },
    )

export const loadCampaign = (id) =>
    djangioActionCreators.resourceGetDetail(
        DjangIO.app.grassroots.campaign.models.Campaign.objects.filter({
            dehydrate_extra: ["custom_interaction_types"],
        }),
        loadCampaignTypes,
        id,
    )

export const createDefaultSavedSearch = (campaignId, campaign) => (dispatch) => {
    dispatch({
        type: CampaignActionTypes.CREATE_DEFAULT_SAVED_SEARCH_START,
    })
    const defaultKwargs = {
        user_id: Userdata.id,
        data_type: DjangIO.app.models.QuorumDataType.person.value,
        filters: [
            {
                ph_minor_role_type: [],
            },
        ],
        name: "Default Lists",
    }
    return DjangIO.app.userdata.models.SafedSearch.objects
        .create(defaultKwargs)
        .POST()
        .then((savedSearch) => {
            // update the campaign with the new saved search
            dispatch(editCampaign("default_targets_saved_search", savedSearch))
        })
        .catch((error) => {
            BACKENDERROR(error)
            dispatch({ type: CampaignActionTypes.CREATE_DEFAULT_SAVED_SEARCH_FAIL, error })
        })
}

export const updateCampaignStore = (key, value) => ({ type: CampaignActionTypes.UPDATE_CAMPAIGN_STORE, key, value })

export const editCampaign = (key, value, additionalValues) => ({
    type: CampaignActionTypes.EDIT_CAMPAIGN,
    key,
    value,
    additionalValues,
})

/**
 * This function patches a campaign
 * @name patchCampaign
 * @function
 * @param {Integer} argument - The id of the campaign to patch
 * @param {Object} campaign - an object containing fields on the campaign model to patch
 * @returns {Promise}
 */
export const patchCampaign = (id, campaign) => (dispatch, getState) => {
    // If 'campaign' object passed to function is immutable, convert to non-immutable object
    const nonImmutableCampaign = campaign && campaign.toJS ? campaign.toJS() : campaign

    const kwargs = { ...nonImmutableCampaign }

    const {
        shouldSendConfirmationEmail,
        confirmationEmailFromAddressUsername,
        confirmationEmailFromAddressDomain,
        confirmationEmailReplyToAddressUsername,
        confirmationEmailReplyToAddressDomain,
    } = getState().campaign.toJS()

    // If choosing to send a confirmation email, append username/domain inputs to a single email address
    if (shouldSendConfirmationEmail) {
        kwargs.conf_email_from_address = [
            confirmationEmailFromAddressUsername,
            confirmationEmailFromAddressDomain,
        ].join("@")
        kwargs.conf_email_reply_to_address = [
            confirmationEmailReplyToAddressUsername,
            confirmationEmailReplyToAddressDomain,
        ].join("@")
    }

    const storeCampaignStatus = getState().campaign.toJS().campaign.status

    if (kwargs.status !== storeCampaignStatus) {
        kwargs.status = storeCampaignStatus
    }

    return dispatch(
        djangioActionCreators.resourcePatchDetail(
            DjangIO.app.grassroots.campaign.models.Campaign,
            patchCampaignTypes,
            id,
            kwargs,
        ),
    )
}

export const reconcileCampaignInteractionTypes = (campaign) => (dispatch) => {
    const nonImmutableCampaign = campaign && campaign.toJS ? campaign.toJS() : campaign
    const { id, custom_interaction_types } = nonImmutableCampaign
    return dispatch(
        djangioActionCreators.sendAction(
            DjangIO.app.grassroots.campaign.models.CampaignInteractionType,
            patchCampaignInteractionTypesActionDict,
            {
                action: "reconcile_campaign_interaction_types",
                kwargs: { campaign_id: id, custom_interaction_types },
                method: "patch",
            },
        ),
    )
}

export const addOptionValue = () => ({
    type: CampaignActionTypes.ADD_TWEET_VALUE,
})

export const updateOptionValue = (optionKey, optionValue) => ({
    type: CampaignActionTypes.UPDATE_TWEET_VALUE,
    optionKey,
    optionValue,
})

export const deleteOptionField = (optionKey) => ({
    type: CampaignActionTypes.DELETE_TWEET_VALUE,
    optionKey,
})

export const launchAdvSearch = (initialSearch) => ({ type: CampaignActionTypes.START_ADV_SELECTION, initialSearch })

export const updateTargetSelection = (filterObj, savedSearchTargetField) => ({
    type: CampaignActionTypes.ADD_ADV_SELECTION,
    filterObj,
    savedSearchTargetField,
})

export function campaignSubmitSearchify({ searchifyFilters }) {
    return (dispatch, getState) => {
        const { initialSearch } = getState().campaign.toJS()

        // change text for input
        let placeholderText = ""
        if (initialSearch.id === null) {
            placeholderText = "Name your selection here"
        } else {
            placeholderText = "Rename your selection here"
        }

        // sweet alert to name selection
        return swal({
            buttons: true,
            title: placeholderText,
            content: {
                element: "input",
                attributes: {
                    placeholder: initialSearch.text,
                },
            },
        }).then((inputValue) => {
            if (!inputValue) {
                swal("Please name your selection")
                return null
            }

            const kwargs = {
                user_id: Userdata.id,
                data_type: initialSearch.quorumDataType,
                filters: searchifyFilters["querysetList"],
                added_ids: searchifyFilters["addedIds"],
                deleted_ids: searchifyFilters["deletedIds"],
                name: inputValue,
                always_forced_regions: Userdata.current_regions_values,
            }

            DjangIO.app.userdata.models.SafedSearch.objects
                .create(kwargs)
                .POST()
                .then((savedSearch) => {
                    // NOTE: this relevant reducer is in the campaign reducer, despite the
                    // searchActionTypes origin!
                    dispatch({ type: searchifyActionTypes.SEARCHIFY_SUBMIT })
                    // send savedSearch to campaign store and edit campaign
                    dispatch(updateTargetSelection(savedSearch, initialSearch.savedSearchTargetField))
                })

            return true
        })
    }
}

export const createEmailFromCampaign = (campaignId, campaignName) => (dispatch) =>
    dispatch(outboxActionCreators.createBulkEmail()).then((bulkEmail) => {
        dispatch(outboxActionCreators.setIsFromCampaign(bulkEmail.id))
        dispatch(outboxActionCreators.segueToOutbox({ bulkEmailId: bulkEmail.id }))
        dispatch(outboxActionCreators.addCampaignAsSelection(campaignId, campaignName))
    })

/**
 * Action creator for text profile action button which creates a new BulkSMS
 * with the campaign's targeted supporters as recipients and segues to the
 * texting compose form for that text
 *
 * @name createTextFromCampaign
 * @function
 * @param {Number} campaignID - ID of the campaign to create the text for
 * @param {String} campaignName - name of the campaign
 * @returns {Function}
 */
export const createTextFromCampaign = (campaignId, campaignName) => (dispatch) =>
    dispatch(outboundTextingActionCreators.createTextForCampaign(campaignId, campaignName))

// profiles-new
const { CampaignStatus } = DjangIO.app.grassroots.campaign.types

export const deleteDraft =
    ({ id, actionCenterId }) =>
    async (dispatch) => {
        const willDelete = await swal(swalConfigs.deleteDraft.prompt)
        if (willDelete) {
            try {
                dispatch(editCampaign("status", CampaignStatus.discarded_draft.value))
                await dispatch(patchCampaign(id, { status: CampaignStatus.discarded_draft.value }))
                swal.stopLoading()
                swal.close()
                dispatch(segue(getActionCenterPaths(actionCenterId)._actionCenterCampaigns))
            } catch (error) {
                Sentry.captureException(error)
                swal(swalConfigs.deleteDraft.fail)
            }
        }
    }

/**
 * Action Creator that updates the usable placeholders for a campaign
 *
 * @param {Number} id campaign id
 * @returns {Promise} returns the promise for the request that contains all
 the valid placeholders for the user
 */
const { Campaign } = DjangIO.app.grassroots.campaign.models
export const loadPlaceholders = (id) =>
    djangioActionCreators.resourceGetDetail(
        Campaign.objects.action("get_placeholders_for_campaign"),
        loadPlaceholdersActionDict,
        id,
    )

/**
 * Action Creator that checks whether the placeholders used in text fields that
 can use placeholders (selected via selectTextToValidate) contain only valid
 placeholders
 *
 * @param {Number} id campaign id
 * @returns {Promise} returns { isValid, errors } from the request
 */
export const validatePlaceholders = (id) => async (dispatch, getState) =>
    dispatch(
        djangioActionCreators.resourcePatchAction(Campaign.objects, validatePlaceholdersActionDict, id, {
            actionName: "are_placeholders_valid_for_campaign",
            text: selectTextToValidate(getState()),
        }),
    )

export const loadGrassrootsRegistrationPage = (id) =>
    djangioActionCreators.resourceGetDetail(
        DjangIO.app.grassroots.models.GrassrootsRegistrationPage,
        loadGrassrootsRegistrationPageActionDict,
        id,
        {},
        true,
    )

export const clearGrassrootsRegistrationPage = () => ({ type: CampaignActionTypes.CLEAR_GRASSROOTS_REGISTRATION_PAGE })

export const createGrassrootsRegistrationPage = (grassrootsRegistrationPage) => async (dispatch) => {
    const result = await dispatch(
        djangioActionCreators.resourcePostList(
            DjangIO.app.grassroots.models.GrassrootsRegistrationPage,
            createGrassrootsRegistrationPageActionDict,
            grassrootsRegistrationPage,
        ),
    )
    dispatch(sendUserAction(DjangIO.app.useractions.types.UserActionType.create_registration))
    return result
}

export const patchGrassrootsRegistrationPage = (id, grassrootsRegistrationPage) => async (dispatch) => {
    const result = await dispatch(
        djangioActionCreators.resourcePatchDetail(
            DjangIO.app.grassroots.models.GrassrootsRegistrationPage,
            patchGrassrootsRegistrationPageActionDict,
            id,
            grassrootsRegistrationPage,
        ),
    )
    dispatch(sendUserAction(DjangIO.app.useractions.types.UserActionType.edit_registration, id))
    return result
}

export const archiveGrassrootsRegistrationPage = (id) =>
    djangioActionCreators.resourcePatchDetail(
        DjangIO.app.grassroots.models.GrassrootsRegistrationPage,
        patchGrassrootsRegistrationPageActionDict,
        id,
        { archived: true },
    )

export const setValid = (id, isValid) => ({
    type: CampaignActionTypes.SET_VALID,
    isValid,
    id,
})

export const validRegulation = (id, regulation_id) => async (dispatch, getState) =>
    dispatch(
        djangioActionCreators.resourcePatchAction(Campaign.objects, validateRegulationIdActionDict, id, {
            actionName: "validate_regulation_id",
            kwargs: { regulation_id },
        }),
    )
